import {
    Avatar,
    Divider,
    Grid,
    MenuItem
} from '@mui/material';
import { useTheme } from '@mui/styles';
import { AdminPermission, SecondaryApiTypeEnum } from '@tassat/common-definitions';
import {
    AddressBook as AddressBookIcon,
    ApiPair as ApiPairIcon,
    Document as DocumentIcon,
    Support as SupportIcon,
    Wallet as WalletIcon
} from '@tassat/icon';
import { SideBarTile } from '@tassat/menu';
import React from 'react';

import { NON_ALPHANUMERIC_REGEX } from 'Lib/constant';
import { getAdminPermissionsByFeatures } from 'Lib/permissionHelper';
import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';
import { useAppSelector } from 'Reducers/store/hooks';
import { getCurrentRole, getUser } from 'Selectors/common';
import { getApiTypesByUserId } from 'Selectors/userApiTypes';
import { useFeatures } from 'SharedComponents/hooks/useFeatures';
import UserMenu from 'SharedComponents/users/userMenu';

import { muiStyles } from './sidebarsStyles';

import { SidebarProps } from '.';

const baseClientItems: any = [
    {
        name: 'Accounts',
        url: '/accounts',
        Icon: WalletIcon
    },
    {
        name: 'Address Book',
        url: '/addressbook',
        Icon: AddressBookIcon
    },
    {
        name: 'Document Center',
        url: '/documentcenter',
        Icon: DocumentIcon
    },
    {
        name: 'Support',
        url: '/support',
        Icon: SupportIcon
    }
];

const apiClientItem = {
    name: 'API Pair',
    url: '/api',
    Icon: ApiPairIcon
};

export function Sidebar (props: SidebarProps) {
    const {
        admin,
        location
    } = props;

    const user = useAppSelector(getUser);
    const userRole = useAppSelector(getCurrentRole);
    const userApiTypes = useAppSelector((state) => getApiTypesByUserId(state, false, state.user?.UserId || ''));
    const classes = muiStyles(props);
    const [ userMenuAnchor, setUserMenuAnchorEl ] = React.useState<null | HTMLElement>(null);
    const handleUserMenuClose = () => setUserMenuAnchorEl(null);
    const muiTheme = useTheme<ApplicationMuiTheme>();
    const [features] = useFeatures();

    const {
        useGlobalHeader,
        useClientGlobalHeader
    } = muiTheme?.app?.clientTheme.layoutSettings || {};

    const isGlobalHeaderEnabled = React.useMemo(() => {
        if (admin && useGlobalHeader) return true;
        if (!admin && useClientGlobalHeader) return true;

        return false;
    }, [ admin, useGlobalHeader, useClientGlobalHeader ]);

    const sidebarItems = React.useMemo(() => {
        if (admin) {
            const adminPermissions = getAdminPermissionsByFeatures(muiTheme);

            return userRole ?
                adminPermissions.filter((item: AdminPermission) => item.url && item.permission.some(perm => userRole[perm]))
                : [];
        }

        const clientItems = [...baseClientItems];
        if (
            user?.IsTokenAPIUser ||
            (features.fireblocks.api && userApiTypes
                .some((apiType) => apiType.APISecondaryConnectionType === SecondaryApiTypeEnum.FIREBLOCKS))
        ) {
            // Use splice to put API link before Support link
            clientItems.splice(-1, 0, apiClientItem);
        }

        return clientItems;
    }, [ admin, user?.IsTokenAPIUser, userRole, userApiTypes ]);

    const userMenuItems = React.useMemo(() => {
        const menuItems: Array<Array<React.JSX.Element>> = [];

        if (user?.IsAdmin && muiTheme?.app.tassat.legalFiles.length > 0) {
            menuItems.push(muiTheme.app.tassat.legalFiles.map((file, i) => {
                return (
                    <a
                        className={classes.menuLink}
                        href={file.url}
                        key={file.url}
                        rel='noreferrer'
                        target='_blank'
                    >
                        <MenuItem
                            id={`sidebar_user_menu_${file.fileName.replace(NON_ALPHANUMERIC_REGEX, '')}`}
                            key={i + '_' + file.fileName}
                        >
                            {file.fileDescription}
                        </MenuItem>
                    </a>
                );
            }));
        }

        return menuItems;
    }, [ user?.IsAdmin, muiTheme ]);

    return (
        <Grid
            className={classes.outerContainer}
            container
            direction='column'
            spacing={0}
            wrap='nowrap'
        >
            <Grid
                className={classes.logo}
                item
                md={1}
            />
            <ul className={classes.container}>
                {sidebarItems.map(item => {
                    if (item && item.name) {
                        return (
                            <SideBarTile
                                active={location.pathname}
                                item={item}
                                key={item.name}
                            />
                        );
                    }

                    return null;
                })}
            </ul>
            {(!isGlobalHeaderEnabled) && user &&
                <>
                    <Divider
                        className={classes.divider}
                        light
                        variant='fullWidth'
                    />
                    <Grid
                        container
                        item
                        justifyContent='center'
                        spacing={0}
                        xs={12}
                    >
                        <Avatar
                            className={`${classes.userAvatar} ${userMenuItems.length > 0 ? classes.userMenu : ''}`}
                            onClick={(e) => setUserMenuAnchorEl(e.currentTarget)}
                            title={`${user.FirstName} ${user.LastName}`}
                        >
                            {`${user.FirstName.substr(0, 1)}${user.LastName.substr(0, 1)}`}
                        </Avatar>
                        <UserMenu
                            handleMenuClose={handleUserMenuClose}
                            menuAnchor={userMenuAnchor}
                            menuItems={userMenuItems}
                        />
                    </Grid>
                </>}
        </Grid>
    );
}